// ─────────────────────────────────────────────
// HubWire Auth Context
// Mock auth layer using React context/provider
// Replace getCurrentAdminUser/loginAdmin/logoutAdmin
// with real auth calls (Cloudflare Access, JWT, etc.)
// ─────────────────────────────────────────────

import React, { createContext, useContext, useEffect, useState } from 'react';
import type { AdminUser, AuthState, LoginCredentials } from '../../types';
import {
  getCurrentAdminUser,
  loginAdmin,
  logoutAdmin,
} from '../../services/apiClient';

// ─────────────────────────────────────────────
// Context shape
// ─────────────────────────────────────────────
interface AuthContextValue extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error: string | null }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  // Check for existing session on mount
  useEffect(() => {
    getCurrentAdminUser().then((res) => {
      setState({
        user: res.data,
        isAuthenticated: res.success && res.data !== null,
        isLoading: false,
      });
    });
  }, []);

  const login = async (credentials: LoginCredentials) => {
    const res = await loginAdmin(credentials);
    if (res.success && res.data) {
      setState({ user: res.data, isAuthenticated: true, isLoading: false });
      return { success: true, error: null };
    }
    return { success: false, error: res.error };
  };

  const logout = async () => {
    await logoutAdmin();
    setState({ user: null, isAuthenticated: false, isLoading: false });
  };

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// ─────────────────────────────────────────────
// Role helpers
// ─────────────────────────────────────────────
export function canEdit(user: AdminUser | null): boolean {
  if (!user) return false;
  return ['owner', 'admin', 'editor'].includes(user.role);
}

export function canManage(user: AdminUser | null): boolean {
  if (!user) return false;
  return ['owner', 'admin'].includes(user.role);
}

export function isOwner(user: AdminUser | null): boolean {
  return user?.role === 'owner';
}
